import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmationModal from '@/components/ConfirmationModal';
import {
  RefreshCw, Plus, Trash2, Activity, Wifi, WifiOff, ArrowLeft,
  Server, CheckCircle, XCircle, AlertTriangle, Clock, Zap, Search,
  ChevronRight, Settings, List, FileText,
} from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const CONNECTOR_TYPE_LABELS: Record<string, string> = {
  okta: 'Okta',
  aws_iam: 'AWS IAM',
  github: 'GitHub',
  m365: 'Microsoft 365',
  companies_house: 'Companies House',
};

const CONNECTOR_TYPE_COLORS: Record<string, string> = {
  okta: 'bg-indigo-100 text-indigo-800',
  aws_iam: 'bg-orange-100 text-orange-800',
  github: 'bg-gray-100 text-gray-800',
  m365: 'bg-blue-100 text-blue-800',
  companies_house: 'bg-green-100 text-green-800',
};

const STATUS_COLORS: Record<string, string> = {
  configuring: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  disabled: 'bg-gray-100 text-gray-600',
};

const FINDING_STATUS_COLORS: Record<string, string> = {
  pass: 'bg-green-100 text-green-800',
  fail: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
};

const AUTH_TYPES = ['oauth2', 'api_key', 'bearer_token', 'basic', 'aws_role', 'certificate'];

interface Connector {
  id: number;
  name: string;
  connector_type: string;
  status: string;
  description?: string;
  auth_type?: string;
  last_synced?: string;
  health_status?: string;
  created_at: string;
}

interface Finding {
  id: number;
  connector_id: number;
  finding_key: string;
  status: string;
  confidence: number;
  details?: string;
  last_checked: string;
}

interface SyncLog {
  id: number;
  connector_id: number;
  started_at: string;
  completed_at?: string;
  duration?: number;
  status: string;
  findings_count: number;
  error?: string;
}

interface ConnectorType {
  type: string;
  name: string;
  description: string;
  auth_types: string[];
}

type View = 'list' | 'detail' | 'create';
type DetailTab = 'overview' | 'findings' | 'sync-logs';

const ConnectorsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [connectorTypes, setConnectorTypes] = useState<ConnectorType[]>([]);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, healthy: 0, unhealthy: 0, totalFindings: 0, failedFindings: 0 });

  // Create wizard
  const [wizardStep, setWizardStep] = useState(0);
  const [createType, setCreateType] = useState('');
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createAuthType, setCreateAuthType] = useState('api_key');
  const [createCredentials, setCreateCredentials] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<Connector | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [connRes, typesRes, healthRes, findingsRes] = await Promise.all([
        fetch('/api/connectors', { headers: authHeaders() }),
        fetch('/api/connectors/types', { headers: authHeaders() }),
        fetch('/api/connectors/health', { headers: authHeaders() }),
        fetch('/api/connectors/findings', { headers: authHeaders() }),
      ]);

      let list: Connector[] = [];
      let typesList: ConnectorType[] = [];
      let allFindings: Finding[] = [];

      if (connRes.ok) {
        try {
          const connData = await connRes.json();
          const raw = connData?.connectors ?? connData;
          list = Array.isArray(raw) ? raw : [];
        } catch { list = []; }
      }

      if (typesRes.ok) {
        try {
          const typesData = await typesRes.json();
          const raw = typesData?.types ?? typesData;
          typesList = Array.isArray(raw) ? raw : [];
        } catch { typesList = []; }
      }

      // healthRes is not used for state directly, just ignore if failed
      if (healthRes.ok) {
        try { await healthRes.json(); } catch { /* ignored */ }
      }

      if (findingsRes.ok) {
        try {
          const findingsData = await findingsRes.json();
          const raw = findingsData?.findings ?? findingsData;
          allFindings = Array.isArray(raw) ? raw : [];
        } catch { allFindings = []; }
      }

      setConnectors(list);
      setConnectorTypes(typesList);

      const healthy = list.filter((c: Connector) => c.status === 'active').length;
      setStats({
        total: list.length,
        healthy,
        unhealthy: list.length - healthy,
        totalFindings: allFindings.length,
        failedFindings: allFindings.filter((f: Finding) => f.status === 'fail').length,
      });
    } catch (e: any) {
      setConnectors([]);
      setConnectorTypes([]);
      setStats({ total: 0, healthy: 0, unhealthy: 0, totalFindings: 0, failedFindings: 0 });
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openDetail = async (conn: Connector) => {
    setSelectedConnector(conn);
    setDetailTab('overview');
    setView('detail');
    try {
      const [findRes, logRes] = await Promise.all([
        fetch(`/api/connectors/findings?connector_id=${conn.id}`, { headers: authHeaders() }),
        fetch(`/api/connectors/${conn.id}/sync-logs`, { headers: authHeaders() }),
      ]);

      if (findRes.ok) {
        try {
          const fData = await findRes.json();
          const raw = fData?.findings ?? fData;
          setFindings(Array.isArray(raw) ? raw : []);
        } catch { setFindings([]); }
      } else {
        setFindings([]);
      }

      if (logRes.ok) {
        try {
          const lData = await logRes.json();
          const raw = lData?.logs ?? lData;
          setSyncLogs(Array.isArray(raw) ? raw : []);
        } catch { setSyncLogs([]); }
      } else {
        setSyncLogs([]);
      }
    } catch {
      setFindings([]);
      setSyncLogs([]);
    }
  };

  const triggerSync = async (connId: number) => {
    try {
      await fetch(`/api/connectors/${connId}/sync`, { method: 'POST', headers: authHeaders() });
      if (selectedConnector?.id === connId) openDetail(selectedConnector);
      else loadAll();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const checkHealth = async (connId: number) => {
    try {
      const res = await fetch(`/api/connectors/${connId}/health`, { headers: authHeaders() });
      const data = await res.json();
      setConnectors(prev => prev.map(c => c.id === connId ? { ...c, health_status: data.status || data.health } : c));
    } catch { /* ignore */ }
  };

  const deleteConnector = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`/api/connectors/${deleteTarget.id}`, { method: 'DELETE', headers: authHeaders() });
      setDeleteTarget(null);
      if (selectedConnector?.id === deleteTarget.id) { setView('list'); setSelectedConnector(null); }
      loadAll();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const createConnector = async () => {
    if (!createType || !createName.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/connectors', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: createName,
          connector_type: createType,
          description: createDesc,
          auth_type: createAuthType,
          credentials: createCredentials,
        }),
      });
      if (!res.ok) throw new Error('Failed to create connector');
      resetCreate();
      setView('list');
      loadAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreate = () => {
    setWizardStep(0);
    setCreateType('');
    setCreateName('');
    setCreateDesc('');
    setCreateAuthType('api_key');
    setCreateCredentials({});
  };

  const STAT_CARDS = [
    { label: 'Total Connectors', value: stats.total, icon: Server, color: 'text-blue-600' },
    { label: 'Healthy', value: stats.healthy, icon: CheckCircle, color: 'text-green-600' },
    { label: 'Unhealthy', value: stats.unhealthy, icon: XCircle, color: 'text-red-600' },
    { label: 'Total Findings', value: stats.totalFindings, icon: Search, color: 'text-purple-600' },
    { label: 'Failed Findings', value: stats.failedFindings, icon: AlertTriangle, color: 'text-orange-600' },
  ];

  if (loading && connectors.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {view !== 'list' && (
            <button onClick={() => { setView('list'); setSelectedConnector(null); resetCreate(); }} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {view === 'detail' ? selectedConnector?.name : view === 'create' ? 'Add Connector' : 'Connectors'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {view === 'list' ? 'Manage integrations and data connectors' : view === 'create' ? 'Configure a new integration' : 'Connector details and sync history'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {view === 'list' && (
            <>
              <button onClick={loadAll} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
                <RefreshCw size={16} />
              </button>
              <button onClick={() => setView('create')} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                <Plus size={16} /> Add Connector
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {STAT_CARDS.map(s => (
              <div key={s.label} className="bg-white border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon size={16} className={s.color} />
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Connector List */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Synced</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Health</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {connectors.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No connectors configured</td></tr>
                  ) : connectors.map(conn => (
                    <tr key={conn.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button onClick={() => openDetail(conn)} className="text-blue-600 hover:underline font-medium">
                          {conn.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONNECTOR_TYPE_COLORS[conn.connector_type] || 'bg-gray-100 text-gray-700'}`}>
                          {CONNECTOR_TYPE_LABELS[conn.connector_type] || conn.connector_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[conn.status] || 'bg-gray-100 text-gray-600'}`}>
                          {conn.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {conn.last_synced ? new Date(conn.last_synced).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${conn.status === 'active' ? 'bg-green-500' : conn.status === 'error' ? 'bg-red-500' : 'bg-gray-400'}`} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => triggerSync(conn.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Sync">
                            <Zap size={14} />
                          </button>
                          <button onClick={() => checkHealth(conn.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Health Check">
                            <Activity size={14} />
                          </button>
                          <button onClick={() => setDeleteTarget(conn)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* DETAIL VIEW */}
      {view === 'detail' && selectedConnector && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 border-b">
            {(['overview', 'findings', 'sync-logs'] as DetailTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setDetailTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${detailTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {tab === 'sync-logs' ? 'Sync Logs' : tab}
              </button>
            ))}
          </div>

          {detailTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border rounded-xl p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-medium">{CONNECTOR_TYPE_LABELS[selectedConnector.connector_type] || selectedConnector.connector_type}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Status</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedConnector.status]}`}>{selectedConnector.status}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Auth Type</span><span>{selectedConnector.auth_type || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Last Synced</span><span>{selectedConnector.last_synced ? new Date(selectedConnector.last_synced).toLocaleString() : 'Never'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Created</span><span>{new Date(selectedConnector.created_at).toLocaleDateString()}</span></div>
                </div>
                {selectedConnector.description && <p className="text-sm text-gray-600 pt-2 border-t">{selectedConnector.description}</p>}
              </div>
              <div className="bg-white border rounded-xl p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Actions</h3>
                <div className="space-y-2">
                  <button onClick={() => triggerSync(selectedConnector.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    <Zap size={16} /> Trigger Sync
                  </button>
                  <button onClick={() => checkHealth(selectedConnector.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border rounded-lg hover:bg-gray-50">
                    <Activity size={16} /> Health Check
                  </button>
                  <button onClick={() => setDeleteTarget(selectedConnector)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                    <Trash2 size={16} /> Delete Connector
                  </button>
                </div>
              </div>
            </div>
          )}

          {detailTab === 'findings' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Finding Key</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Checked</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {findings.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No findings</td></tr>
                  ) : findings.map(f => (
                    <tr key={f.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{f.finding_key}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FINDING_STATUS_COLORS[f.status] || 'bg-gray-100 text-gray-600'}`}>{f.status}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{f.confidence != null ? `${Math.round(f.confidence * 100)}%` : '-'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{new Date(f.last_checked).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detailTab === 'sync-logs' && (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {syncLogs.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No sync logs</td></tr>
                  ) : syncLogs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs">{new Date(log.started_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-600">{log.duration ? `${log.duration}s` : '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.status === 'success' ? 'bg-green-100 text-green-800' : log.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{log.findings_count ?? '-'}</td>
                      <td className="px-4 py-3 text-red-600 text-xs truncate max-w-[200px]">{log.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* CREATE WIZARD */}
      {view === 'create' && (
        <div className="bg-white border rounded-xl p-6 max-w-2xl">
          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-6">
            {['Select Type', 'Details', 'Authentication'].map((step, i) => (
              <React.Fragment key={step}>
                {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
                <span className={`text-sm font-medium ${wizardStep === i ? 'text-blue-600' : wizardStep > i ? 'text-green-600' : 'text-gray-400'}`}>
                  {step}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Step 0: Select type */}
          {wizardStep === 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Choose Connector Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {connectorTypes.length > 0 ? connectorTypes.map(ct => (
                  <button
                    key={ct.type}
                    onClick={() => { setCreateType(ct.type); setWizardStep(1); }}
                    className={`p-4 border rounded-xl text-left hover:border-blue-400 hover:bg-blue-50 transition ${createType === ct.type ? 'border-blue-500 bg-blue-50' : ''}`}
                  >
                    <p className="text-sm font-semibold text-gray-900">{ct.name || CONNECTOR_TYPE_LABELS[ct.type] || ct.type}</p>
                    <p className="text-xs text-gray-500 mt-1">{ct.description || ''}</p>
                  </button>
                )) : Object.entries(CONNECTOR_TYPE_LABELS).map(([type, label]) => (
                  <button
                    key={type}
                    onClick={() => { setCreateType(type); setWizardStep(1); }}
                    className={`p-4 border rounded-xl text-left hover:border-blue-400 hover:bg-blue-50 transition ${createType === type ? 'border-blue-500 bg-blue-50' : ''}`}
                  >
                    <p className="text-sm font-semibold text-gray-900">{label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Name and description */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Connector Details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. Production Okta" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={createDesc} onChange={e => setCreateDesc(e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-between">
                <button onClick={() => setWizardStep(0)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Back</button>
                <button onClick={() => setWizardStep(2)} disabled={!createName.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">Next</button>
              </div>
            </div>
          )}

          {/* Step 2: Auth credentials */}
          {wizardStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Authentication</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Auth Type</label>
                <select value={createAuthType} onChange={e => setCreateAuthType(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                  {AUTH_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
              </div>
              {createAuthType === 'api_key' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <input type="password" value={createCredentials.api_key || ''} onChange={e => setCreateCredentials({ ...createCredentials, api_key: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
              {createAuthType === 'bearer_token' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Token</label>
                  <input type="password" value={createCredentials.token || ''} onChange={e => setCreateCredentials({ ...createCredentials, token: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
              {createAuthType === 'oauth2' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                    <input type="text" value={createCredentials.client_id || ''} onChange={e => setCreateCredentials({ ...createCredentials, client_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                    <input type="password" value={createCredentials.client_secret || ''} onChange={e => setCreateCredentials({ ...createCredentials, client_secret: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
              {createAuthType === 'basic' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <input type="text" value={createCredentials.username || ''} onChange={e => setCreateCredentials({ ...createCredentials, username: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input type="password" value={createCredentials.password || ''} onChange={e => setCreateCredentials({ ...createCredentials, password: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
              {createAuthType === 'aws_role' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role ARN</label>
                    <input type="text" value={createCredentials.role_arn || ''} onChange={e => setCreateCredentials({ ...createCredentials, role_arn: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">External ID</label>
                    <input type="text" value={createCredentials.external_id || ''} onChange={e => setCreateCredentials({ ...createCredentials, external_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <button onClick={() => setWizardStep(1)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Back</button>
                <button onClick={createConnector} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Connector'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteConnector}
        title="Delete Connector"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This will remove all associated findings and sync history.`}
        type="danger"
        confirmText="Delete"
      />
    </div>
  );
};

export default ConnectorsDashboard;
